import React, { useEffect, useRef, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Box, Button, Stepper, Step, StepLabel, TextField, Typography, Alert, CircularProgress } from '@mui/material';
import { supabase } from '../../lib/supabase';
import CameraCaptureModal from '../Media/CameraCaptureModal';

interface VerificationModalProps {
  open: boolean;
  onClose: () => void;
  userId?: string | null;
}

const steps = ['Profildaten', 'Identität', 'Foto', 'Bestätigung'];

const VerificationModal: React.FC<VerificationModalProps> = ({ open, onClose, userId }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({ fullName: '', address: '', idNumber: '' });
  // ID document photo (upload or capture)
  const [idPhotoFile, setIdPhotoFile] = useState<File | null>(null);
  const [idPhotoPreview, setIdPhotoPreview] = useState<string | null>(null);
  // Profile photo (must capture)
  const [profilePhotoFile, setProfilePhotoFile] = useState<File | null>(null);
  const [profilePhotoPreview, setProfilePhotoPreview] = useState<string | null>(null);
  const [settings, setSettings] = useState<any>(null);
  const [cameraOpen, setCameraOpen] = useState<null | 'id' | 'profile'>(null);
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string | undefined>(undefined);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [previewReady, setPreviewReady] = useState(false);

  useEffect(() => {
    // Load website settings to determine upload target
    fetch('/website-settings.json')
      .then(r => r.json())
      .then(setSettings)
      .catch(() => setSettings(null));
  }, []);

  const startCamera = async (mode: 'id' | 'profile') => {
    setError(null);
    setCameraOpen(mode);
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    setCameraOpen(null);
  };

  const dataUrlToFile = async (dataUrl: string, fileName: string): Promise<File> => {
    const res = await fetch(dataUrl);
    const blob = await res.blob();
    return new File([blob], fileName, { type: blob.type || 'image/jpeg' });
  };

  const capturePhoto = async () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    const w = video.videoWidth || 720;
    const h = video.videoHeight || 480;
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(video, 0, 0, w, h);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
    const file = await dataUrlToFile(dataUrl, `capture_${Date.now()}.jpg`);
    if (cameraOpen === 'id') {
      setIdPhotoFile(file);
      setIdPhotoPreview(dataUrl);
    } else if (cameraOpen === 'profile') {
      setProfilePhotoFile(file);
      setProfilePhotoPreview(dataUrl);
    }
    stopCamera();
  };

  const handleNext = async () => {
    setError(null);
    // Require a captured profile photo before leaving the photo step
    if (activeStep === 2) {
      if (!profilePhotoFile) {
        setError('Bitte mache ein Profilfoto, um fortzufahren.');
        return;
      }
    }
    if (activeStep < steps.length - 1) {
      setActiveStep((s) => s + 1);
      return;
    }
    // Finalize: mark user as verified
    try {
      setLoading(true);
      if (!userId) throw new Error('Kein Benutzer erkannt');
      let idPhotoUrl: string | undefined;
      let storageProvider: 'supabase' | 'backblaze' | undefined;
      let profilePhotoUrl: string | undefined;

      // Upload photo if provided
      if (idPhotoFile) {
        const fileName = `${Date.now()}_${idPhotoFile.name.replace(/\s+/g, '_')}`;
        const target = settings?.uploadTarget || 'supabase';
        if (target === 'supabase') {
          const key = `${userId}/${fileName}`;
          const { data: up, error: upErr } = await supabase.storage
            .from('verification-pictures')
            .upload(key, idPhotoFile, { upsert: true, cacheControl: '3600', contentType: idPhotoFile.type });
          if (upErr) throw upErr;
          const { data: pub } = supabase.storage
            .from('verification-pictures')
            .getPublicUrl(key);
          idPhotoUrl = pub?.publicUrl;
          storageProvider = 'supabase';
        } else {
          // Backblaze upload via backend endpoint (configure in website-settings.json as backblaze.uploadApiUrl)
          const uploadApiUrl = settings?.backblaze?.uploadApiUrl || '/api/b2-upload.php';
          const formData = new FormData();
          formData.append('file', idPhotoFile);
          formData.append('userId', userId);
          formData.append('folder', `verification-pictures/${userId}`);
          const { data: { session } } = await supabase.auth.getSession();
          const resp = await fetch(uploadApiUrl, { method: 'POST', body: formData, headers: session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : undefined });
          if (!resp.ok) throw new Error('Backblaze Upload fehlgeschlagen');
          const json = await resp.json();
          idPhotoUrl = json.publicUrl || json.signedUrl || json.objectKey || '';
          if (!idPhotoUrl) throw new Error('Kein Bild-URL von Backblaze erhalten');
          storageProvider = 'backblaze';
        }
      }

      // Upload profile photo (capture only) to verification-pictures (same as ID)
      if (profilePhotoFile) {
        const fileName = `${Date.now()}_${(profilePhotoFile.name || 'profile.jpg').replace(/\s+/g, '_')}`;
        const target = settings?.uploadTarget || 'supabase';
        if (target === 'supabase') {
          const key = `${userId}/profile_${fileName}`;
          const { error: upErr } = await supabase.storage
            .from('verification-pictures')
            .upload(key, profilePhotoFile, { upsert: true, cacheControl: '3600', contentType: profilePhotoFile.type });
          if (upErr) throw upErr;
          const { data: pub } = supabase.storage
            .from('verification-pictures')
            .getPublicUrl(key);
          profilePhotoUrl = pub?.publicUrl;
        } else {
          const uploadApiUrl = settings?.backblaze?.uploadApiUrl || '/api/b2-upload.php';
          const formData = new FormData();
          formData.append('file', profilePhotoFile);
          formData.append('userId', userId);
          formData.append('folder', `verification-pictures/${userId}`);
          const { data: { session } } = await supabase.auth.getSession();
          const resp = await fetch(uploadApiUrl, { method: 'POST', body: formData, headers: session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : undefined });
          if (!resp.ok) throw new Error('Backblaze Upload fehlgeschlagen');
          const json = await resp.json();
          profilePhotoUrl = json.publicUrl || json.signedUrl || json.objectKey || '';
          if (!profilePhotoUrl) throw new Error('Kein Bild-URL von Backblaze erhalten');
          storageProvider = 'backblaze';
        }
      }

      // Insert verification info
      const { error: vErr } = await supabase.from('verification_info').insert({
        user_id: userId,
        id_number: form.idNumber || null,
        photo_url: idPhotoUrl || profilePhotoUrl || null,
        storage_provider: storageProvider || (settings?.uploadTarget || 'supabase')
      });
      if (vErr) throw vErr;

      // Mark user as verified
      const { error: upsertErr } = await supabase
        .from('users')
        .update({ is_verified: true })
        .eq('id', userId);
      if (upsertErr) throw upsertErr;

      setActiveStep((s) => s + 1);
    } catch (e: any) {
      setError(e?.message || 'Verifizierung fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => setActiveStep((s) => s - 1);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  };

  const handleIdFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] || null;
    setIdPhotoFile(f || null);
    setIdPhotoPreview(f ? URL.createObjectURL(f) : null);
  };

  const handleIdCapture = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] || null;
    setIdPhotoFile(f || null);
    setIdPhotoPreview(f ? URL.createObjectURL(f) : null);
  };

  const handleProfileCapture = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] || null;
    setProfilePhotoFile(f || null);
    setProfilePhotoPreview(f ? URL.createObjectURL(f) : null);
  };

  const renderStep = () => {
    if (activeStep === 0) {
      return (
        <Box sx={{ display: 'grid', gap: 2 }}>
          <TextField label="Vollständiger Name" name="fullName" value={form.fullName} onChange={handleChange} fullWidth />
          <TextField label="Adresse" name="address" value={form.address} onChange={handleChange} fullWidth />
        </Box>
      );
    }
    
    // No inline overlay; we render the dedicated CameraCaptureModal below
    if (activeStep === 1) {
      return (
        <Box sx={{ display: 'grid', gap: 2 }}>
          <TextField label="Ausweis-/ID-Nummer" name="idNumber" value={form.idNumber} onChange={handleChange} fullWidth />
          <Typography variant="body2" color="text.secondary">Bitte stelle sicher, dass deine Daten korrekt sind.</Typography>
        </Box>
      );
    }
    if (activeStep === 2) {
      return (
        <Box sx={{ display: 'grid', gap: 2 }}>
          <Box>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>Personalausweis</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Lade ein Foto hoch oder nimm eines auf.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              <Button component="label" variant="outlined">
                Foto auswählen
                <input hidden type="file" accept="image/*" onChange={handleIdFile} />
              </Button>
              <Button variant="outlined" onClick={() => startCamera('id')}>Foto aufnehmen</Button>
            </Box>
            {idPhotoPreview && (
              <Box sx={{ mt: 1 }}>
                <img src={idPhotoPreview} alt="Ausweis Vorschau" style={{ maxWidth: '100%', borderRadius: 8 }} />
              </Box>
            )}
          </Box>
          <Box>
            <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>Profilfoto</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Bitte mache ein Foto (Upload ist hier deaktiviert).
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <Button variant="contained" onClick={() => startCamera('profile')}>Profilfoto aufnehmen</Button>
            </Box>
            {profilePhotoPreview && (
              <Box sx={{ mt: 1 }}>
                <img src={profilePhotoPreview} alt="Profil Vorschau" style={{ maxWidth: '100%', borderRadius: 8 }} />
              </Box>
            )}
          </Box>
          {/* Backblaze uploads use /api/b2-upload.php by default; no extra config required. */}
        </Box>
      );
    }
    if (activeStep === 3) {
      return (
        <Box sx={{ display: 'grid', gap: 2 }}>
          <Typography>Bereit zur Verifizierung? Klicke auf Fertigstellen, um deinen Account zu verifizieren.</Typography>
        </Box>
      );
    }
    return (
      <Box sx={{ textAlign: 'center', py: 3 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>Verifizierung abgeschlossen</Typography>
        <Typography variant="body2" color="text.secondary">Du kannst jetzt Produkte hochladen.</Typography>
      </Box>
    );
  };

  const completed = activeStep >= steps.length;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Verifizierung</DialogTitle>
      <DialogContent dividers>
        <Stepper activeStep={Math.min(activeStep, steps.length)} alternativeLabel sx={{ mb: 3 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          renderStep()
        )}
      </DialogContent>
      {/* Camera Capture Modal */}
      <CameraCaptureModal
        open={!!cameraOpen}
        title="Foto machen"
        facing={cameraOpen === 'profile' ? 'user' : 'environment'}
        onClose={() => setCameraOpen(null)}
        onCapture={(file, dataUrl) => {
          if (cameraOpen === 'id') {
            setIdPhotoFile(file);
            setIdPhotoPreview(dataUrl);
          } else if (cameraOpen === 'profile') {
            setProfilePhotoFile(file);
            setProfilePhotoPreview(dataUrl);
          }
          setCameraOpen(null);
        }}
      />
      <DialogActions>
        {!completed ? (
          <>
            <Button onClick={onClose}>Abbrechen</Button>
            <Button disabled={activeStep === 0 || loading} onClick={handleBack}>Zurück</Button>
            <Button variant="contained" onClick={handleNext} disabled={loading}>
              {activeStep < steps.length - 1 ? 'Weiter' : 'Fertigstellen'}
            </Button>
          </>
        ) : (
          <Button variant="contained" onClick={onClose}>Schließen</Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default VerificationModal;
